<template>
  <div class="mit-page">
    <v-container fluid class="pa-6">
      
      <!-- GLOBAL FILTERS -->
      <v-card outlined class="mb-6 elevation-1">
        <v-card-title class="grey lighten-4">Global Analytics Filters</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-row align="center">
            <v-col cols="12" md="3">
              <v-text-field v-model="filters.startDate" type="date" label="Start Date" outlined dense hide-details @change="fetchData"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="filters.endDate" type="date" label="End Date" outlined dense hide-details @change="fetchData"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete v-model="filters.csailId" :items="filterOptions.users" label="Specific User" outlined dense clearable hide-details @change="fetchData"></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete v-model="filters.ipAddress" :items="filterOptions.ips" label="Specific IP" outlined dense clearable hide-details @change="fetchData"></v-autocomplete>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- USER STATISTICS SECTION -->
      <div class="text-h5 font-weight-bold mb-4">Session & User Statistics</div>
      <v-row class="mb-8" v-if="userStats">
        <v-col cols="12" md="3">
          <v-card class="pa-4 text-center" outlined>
            <div class="text-overline grey--text">Total Sessions</div>
            <div class="text-h3 primary--text">{{ userStats.total_sessions || 0 }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card class="pa-4 text-center" outlined>
            <div class="text-overline grey--text">Unique Users</div>
            <div class="text-h3 primary--text">{{ userStats.unique_users || 0 }}</div>
            <div class="caption mt-2">
              <v-chip x-small color="success" outlined class="mr-1">Logged In: {{ userStats.logged_in_sessions || 0 }}</v-chip>
              <v-chip x-small color="grey" outlined>Anonymous: {{ userStats.anonymous_sessions || 0 }}</v-chip>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card class="pa-4 text-center" outlined>
            <div class="text-overline grey--text">Unique IPs</div>
            <div class="text-h3 primary--text">{{ userStats.unique_ips || 0 }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card class="pa-4 text-center" outlined>
            <div class="text-overline grey--text">Avg Session Time</div>
            <div class="text-h3 primary--text">{{ Math.round(userStats.avg_session_minutes || 0) }} <span class="text-h6">min</span></div>
          </v-card>
        </v-col>
      </v-row>

      <!-- METRICS SECTION BY CATEGORY -->
      <div class="text-h5 font-weight-bold mb-4">Metrics by Category</div>
      
      <div v-for="(catData, categoryName) in processedCategories" :key="categoryName" class="mb-8">
        <v-card outlined class="elevation-1">
          
          <!-- Category Header & Filter -->
          <v-card-title class="primary white--text d-flex align-center">
            <span class="text-capitalize">{{ formatCategoryName(categoryName) }}</span>
            <v-spacer></v-spacer>
            <v-select 
              v-model="categorySelections[categoryName]" 
              :items="catData.availableMetrics" 
              label="Filter by Metric" 
              @change="fetchLogs(categoryName)"
              dense outlined hide-details solo-inverted flat background-color="white" style="max-width: 250px;">
            </v-select>
          </v-card-title>

          <v-card-text class="pt-6">
            <!-- 50/50 CHART ROW -->
            <v-row>
              <!-- LEFT: LINE CHART (Usage over time) -->
              <v-col cols="12" md="6">
                <MitAnalyticsChartLine 
                  :title="`Usage Over Time (${categorySelections[categoryName] || 'All'})`"
                  labelY="Event Count"
                  labelX="Date"
                  :chartData="getTimeSeriesData(categoryName, catData)"
                />
              </v-col>

              <!-- RIGHT: BAR CHART (Total Occurrences) -->
              <v-col cols="12" md="6">
                <MitAnalyticsChartBar 
                  :title="`Occurrences by Metric (${categorySelections[categoryName] || 'All'})`"
                  labelY="Event Count"
                  labelX="Metric"
                  :chartData="getBarTotalsData(categoryName, catData)"
                />
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>
            
            <!-- BOTTOM FULL WIDTH: DATA TABLE -->
            <div class="text-subtitle-1 font-weight-bold mb-2">Metric Drilldown Logs</div>
            <v-data-table
                :headers="tableHeaders"
                :items="tableLogs[categoryName] || []"
                :options.sync="tableOptions[categoryName]"
                :server-items-length="tableTotals[categoryName] || 0"
                :loading="loadingLogs[categoryName]"
                :items-per-page="5"
                @update:options="fetchLogs(categoryName)"
                class="elevation-1 table-border"
            >
              <template v-slot:item.metric_data="{ item }">
                <pre class="json-payload">{{ formatJson(item.metric_data) }}</pre>
              </template>
              <template v-slot:item.created_dt="{ item }">
                {{ formatDate(item.created_dt) }}
              </template>
              <template v-slot:item.browser_user_agent="{ item }">
                <span class="text-truncate d-inline-block" style="max-width: 200px;" :title="item.browser_user_agent">
                  {{ item.browser_user_agent }}
                </span>
              </template>
            </v-data-table>

          </v-card-text>
        </v-card>
      </div>

      <!-- DEEP USER INSIGHTS SECTION -->
      <v-divider class="my-8"></v-divider>
      <div class="text-h5 font-weight-bold mb-4">Audience Insights</div>
      
      <v-row v-if="userInsights">
        <!-- Top Authenticated Users -->
        <v-col cols="12" md="4">
          <v-card outlined class="elevation-1 h-100">
            <v-card-title class="grey lighten-4 text-subtitle-1 font-weight-bold">Top Authenticated Users</v-card-title>
            <v-list dense>
              <v-list-item v-for="(user, index) in userInsights.topUsers" :key="index" class="border-bottom">
                <v-list-item-avatar color="primary lighten-4" size="32">
                  <span class="primary--text caption font-weight-bold">{{ index + 1 }}</span>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">{{ user.csail_id }}</v-list-item-title>
                  <v-list-item-subtitle>{{ Math.round(user.total_minutes) }} total minutes</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip small color="primary" outlined>{{ user.session_count }} visits</v-chip>
                </v-list-item-action>
              </v-list-item>
              <v-list-item v-if="!userInsights.topUsers.length">
                <v-list-item-content><v-list-item-subtitle>No authenticated sessions found.</v-list-item-subtitle></v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>

        <!-- Top IP Addresses -->
        <v-col cols="12" md="4">
          <v-card outlined class="elevation-1 h-100">
            <v-card-title class="grey lighten-4 text-subtitle-1 font-weight-bold">Top Traffic Sources (IPs)</v-card-title>
            <v-list dense>
              <v-list-item v-for="(ip, index) in userInsights.topIps" :key="index" class="border-bottom">
                <v-list-item-avatar color="green lighten-4" size="32">
                  <v-icon small color="green darken-2">mdi-network</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">{{ ip.ip_address }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip small color="green darken-2" outlined>{{ ip.session_count }} visits</v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>

        <!-- Top Browsers & Devices -->
        <v-col cols="12" md="4">
          <v-card outlined class="elevation-1 h-100">
            <v-card-title class="grey lighten-4 text-subtitle-1 font-weight-bold">Top Browsers & Devices</v-card-title>
            <v-list dense>
              <v-list-item v-for="(agent, index) in userInsights.topAgents" :key="index" class="border-bottom">
                <v-list-item-avatar color="purple lighten-4" size="32">
                  <v-icon small color="purple darken-2">{{ getDeviceIcon(agent.browser_user_agent) }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium text-truncate" :title="agent.browser_user_agent">
                    {{ parseUserAgent(agent.browser_user_agent) }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip small color="purple darken-2" outlined>{{ agent.session_count }} visits</v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>

    </v-container>
  </div>
</template>

<script>
import axios from 'axios';
import MitAnalyticsChartBar from '../../components/MitAnalyticsChartBar.vue';
import MitAnalyticsChartLine from '../../components/MitAnalyticsChartLine.vue';

export default {
  name: 'MitAdminAnalyticsPage',
  components: {
    MitAnalyticsChartBar,
    MitAnalyticsChartLine
  },
  data() {
    return {
      filters: {
        startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().substr(0, 10),
        endDate: new Date().toISOString().substr(0, 10),
        csailId: null,
        ipAddress: null
      },
      filterOptions: { users: [], ips: [] },
      userStats: null,
      userInsights: null, // FIX 1: Defines the missing reactive property for the template
      rawMetrics: [],
      tableLogs: {},
      tableOptions: {},
      tableTotals: {},
      loadingLogs: {},
      
      categorySelections: {},
      chartReactivityKey: 0, // Used by triggerChartReactivity
      
      tableHeaders: [
        { text: 'Timestamp', value: 'created_dt', width: '160px' },
        { text: 'Metric', value: 'metric_name', width: '150px' },
        { text: 'User', value: 'csail_id', width: '120px' },
        { text: 'IP', value: 'ip_address', width: '120px' },
        { text: 'User Agent', value: 'browser_user_agent', width: '200px' },
        { text: 'Payload', value: 'metric_data', sortable: false }
      ]
    };
  },
  computed: {
    processedCategories() {
      const categories = {};
      
      this.rawMetrics.forEach(row => {
        if (!categories[row.category]) {
          categories[row.category] = {
            availableMetrics: ['All'],
            timeSeries: {}, 
            totals: {}      
          };
          
          if (!this.categorySelections[row.category]) {
            this.$set(this.categorySelections, row.category, 'All');
          }
        }
        
        const cat = categories[row.category];
        
        if (!cat.availableMetrics.includes(row.metric_name)) {
          cat.availableMetrics.push(row.metric_name);
          cat.totals[row.metric_name] = 0;
        }
        
        if (!cat.timeSeries[row.event_date]) {
          cat.timeSeries[row.event_date] = {};
        }
        if (!cat.timeSeries[row.event_date][row.metric_name]) {
          cat.timeSeries[row.event_date][row.metric_name] = 0;
        }
        
        const count = parseInt(row.count, 10);
        cat.timeSeries[row.event_date][row.metric_name] += count;
        cat.totals[row.metric_name] += count;
      });

      return categories;
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
    try {
        const res = await axios.get('/api/v2/manager/analytics/dashboard', { params: this.filters });
        this.filterOptions = res.data.filterOptions || { users: [], ips: [] };
        this.userStats = res.data.userStats;
        this.rawMetrics = res.data.rawMetrics || [];
        this.userInsights = res.data.insights || null; 
        
        this.triggerChartReactivity();

        // Trigger a log fetch for every loaded category
        Object.keys(this.processedCategories).forEach(category => {
            this.fetchLogs(category);
        });
        } catch (err) {
        console.error("Failed to load dashboard", err);
        }
    },
    async fetchLogs(categoryName) {
        if (!this.tableOptions[categoryName]) {
            this.$set(this.tableOptions, categoryName, { page: 1, itemsPerPage: 5 });
        }
        
        this.$set(this.loadingLogs, categoryName, true);

        const { page, itemsPerPage } = this.tableOptions[categoryName];
        const metricName = this.categorySelections[categoryName] || 'All';

        try {
        const res = await axios.get('/api/v2/manager/analytics/logs', {
            params: {
            ...this.filters, // Pass global dates, csailId, etc.
            category: categoryName,
            metricName: metricName,
            page,
            itemsPerPage
            }
        });
        
        this.$set(this.tableLogs, categoryName, res.data.logs);
        this.$set(this.tableTotals, categoryName, res.data.total);
        } catch (err) {
        console.error(`Failed to fetch logs for ${categoryName}`, err);
        } finally {
        this.$set(this.loadingLogs, categoryName, false);
        }
    },
    
    // FIX 2: Restores the missing method that handles Chart.js rendering updates
    triggerChartReactivity() {
      this.chartReactivityKey += 1;
    },

    getTimeSeriesData(categoryName, catData) {
      const selected = this.categorySelections[categoryName] || 'All';
      const dates = Object.keys(catData.timeSeries).sort();
      
      // A palette of distinct colors for the different metrics
      const colors = [
        '#1976d2', '#4caf50', '#ff9800', '#f44336', '#9c27b0', 
        '#00bcd4', '#795548', '#607d8b', '#e91e63', '#3f51b5'
      ];

      const datasets = [];

      if (selected === 'All') {
        // Plot a separate line for every available metric (ignoring the 'All' option itself)
        const metricsToPlot = catData.availableMetrics.filter(m => m !== 'All');
        
        metricsToPlot.forEach((metricName, index) => {
          const color = colors[index % colors.length];
          const dataPoints = dates.map(date => catData.timeSeries[date][metricName] || 0);
          
          datasets.push({
            label: metricName,
            data: dataPoints,
            borderColor: color,
            backgroundColor: `${color}33`,
            borderWidth: 2,
            fill: false, // Keep fill false so multiple lines don't create a messy overlapped block
            tension: 0.1
          });
        });
      } else {
        // Plot only the single selected metric
        const dataPoints = dates.map(date => catData.timeSeries[date][selected] || 0);
        datasets.push({
          label: selected,
          data: dataPoints,
          borderColor: colors[0],
          backgroundColor: `${colors[0]}33`,
          borderWidth: 2,
          fill: true, // Single metric looks best with a filled area
          tension: 0.1
        });
      }

      return { labels: dates, datasets: datasets };
    },
    
    getBarTotalsData(categoryName, catData) {
      const selected = this.categorySelections[categoryName] || 'All';
      let labels = [];
      let values = [];

      if (selected === 'All') {
        labels = Object.keys(catData.totals);
        values = labels.map(l => catData.totals[l]);
      } else {
        labels = [selected];
        values = [catData.totals[selected]];
      }

      return { labels: labels, values: values };
    },

    getFilteredLogs(categoryName) {
      const selected = this.categorySelections[categoryName] || 'All';
      return this.rawLogs.filter(log => {
        if (log.category !== categoryName) return false;
        if (selected !== 'All' && log.metric_name !== selected) return false;
        return true;
      });
    },
    
    formatCategoryName(name) {
      if (!name) return '';
      return name.split('_').join(' ');
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const d = new Date(dateString);
      return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
    },
    
    formatJson(data) {
      if (!data) return '{}';
      try {
        return JSON.stringify(data, null, 2);
      } catch (e) {
        return data.toString();
      }
    },

    parseUserAgent(ua) {
      if (!ua) return 'Unknown Device';
      
      let browser = 'Unknown Browser';
      if (ua.includes('Firefox') && !ua.includes('Seamonkey') && !ua.includes('Chrome/')) browser = 'Firefox';
      else if (ua.includes('Seamonkey')) browser = 'Seamonkey';
      else if (ua.includes('Chrome') && !ua.includes('Chromium')) browser = 'Chrome';
      else if (ua.includes('Chromium')) browser = 'Chromium';
      else if (ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium')) browser = 'Safari';
      else if (ua.includes('OPR') || ua.includes('Opera')) browser = 'Opera';
      else if (ua.includes('Edg')) browser = 'Edge';
      else if (ua.includes('MSIE') || ua.includes('Trident/')) browser = 'Internet Explorer';

      let os = 'Unknown OS';
      if (ua.includes('Win')) os = 'Windows';
      else if (ua.includes('Mac')) os = 'Mac OS';
      else if (ua.includes('X11')) os = 'UNIX';
      else if (ua.includes('Linux')) os = 'Linux';
      else if (ua.includes('Android')) os = 'Android';
      else if (ua.includes('like Mac')) os = 'iOS';

      return `${browser} on ${os}`;
    },

    getDeviceIcon(ua) {
      if (!ua) return 'mdi-laptop';
      if (ua.includes('Mobi') || ua.includes('Android') || ua.includes('iPhone')) return 'mdi-cellphone';
      if (ua.includes('Tablet') || ua.includes('iPad')) return 'mdi-tablet';
      return 'mdi-laptop';
    }
  }
};
</script>

<style scoped>
.table-border {
  border: 1px solid #e0e0e0;
}
.json-payload {
  font-size: 11px;
  margin: 0;
  background-color: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
  max-height: 120px;
  overflow-y: auto;
}
</style>